import { z } from "zod";

// T-PL-03 (ADR 0034): public Instagram and Facebook profiles of the lead's
// business, searched by the AI with Apify and kept in the lead's custom fields.

export const socialPlatformSchema = z.enum(["instagram", "facebook"]);
export type SocialPlatform = z.infer<typeof socialPlatformSchema>;

/** Custom-field keys written by the search, shown in their own section. */
export const LEAD_SOCIAL_FIELDS = ["socialProfiles", "socialSearches"] as const;

/** Searches per proposal and per lead: each one costs a few cents on Apify. */
export const SOCIAL_SEARCHES_PER_PROPOSAL = 3;
export const SOCIAL_SEARCHES_PER_LEAD = 6;
export const SOCIAL_PROFILES_PER_LEAD = 6;

/** A profile returned by a search, before the AI decides whether it is the lead's. */
export const socialProfileCandidateSchema = z
  .object({
    url: z.string().url().max(300),
    name: z.string().max(120).nullable(),
    username: z.string().max(100).nullable(),
    bio: z.string().max(500).nullable(),
    category: z.string().max(120).nullable(),
    /** Address of the page, or the places of the latest posts. */
    location: z.string().max(200).nullable(),
    followers: z.number().int().nonnegative().nullable(),
    /** Captions of the latest Instagram posts, newest first (ADR 0064). */
    recentPosts: z.array(z.string().min(1).max(300)).max(5).optional(),
  })
  .strict();
export type SocialProfileCandidate = z.infer<typeof socialProfileCandidateSchema>;

export const leadSocialSearchSchema = z
  .object({
    platform: socialPlatformSchema,
    query: z.string().min(1).max(200),
    status: z.enum(["found", "not_found", "failed"]),
    candidates: z.array(socialProfileCandidateSchema).max(5),
    searchedAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type LeadSocialSearch = z.infer<typeof leadSocialSearchSchema>;

/** A profile the AI judged to be the lead's own. */
export const leadSocialProfileSchema = z
  .object({
    platform: socialPlatformSchema,
    url: z.string().url().max(300),
    name: z.string().max(120).nullable(),
    location: z.string().max(200).nullable(),
    /** Bio and latest captions of the search result, for later proposals (ADR 0064). */
    bio: z.string().max(500).nullable().optional(),
    recentPosts: z.array(z.string().min(1).max(300)).max(5).optional(),
    foundAt: z.string().datetime({ offset: true }),
  })
  .strict();
export type LeadSocialProfile = z.infer<typeof leadSocialProfileSchema>;

/** What the model returns in `socialProfiles`: checked against the searches before saving. */
export const aiSocialProfileSchema = z
  .object({
    platform: socialPlatformSchema,
    url: z.string().trim().min(1).max(300),
    name: z.string().trim().max(120).nullable(),
    location: z.string().trim().max(200).nullable(),
  })
  .strict();
export type AiSocialProfile = z.infer<typeof aiSocialProfileSchema>;

function validEntries<T>(value: unknown, schema: z.ZodType<T>): T[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const parsed = schema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
}

/** Social data saved on a lead; malformed entries are skipped. */
export function readLeadSocial(customFields: Record<string, unknown>): {
  profiles: LeadSocialProfile[];
  searches: LeadSocialSearch[];
} {
  return {
    profiles: validEntries(customFields.socialProfiles, leadSocialProfileSchema),
    searches: validEntries(customFields.socialSearches, leadSocialSearchSchema),
  };
}

/** The lead's custom fields without the social keys. */
export function withoutLeadSocialFields(
  customFields: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(customFields).filter(
      ([key]) => !(LEAD_SOCIAL_FIELDS as readonly string[]).includes(key),
    ),
  );
}

/**
 * Comparable form of a search: lower case, single spaces, no "@", no protocol,
 * "www." or trailing slash, so "@Maneema.ac" and "maneema.ac" are one search.
 */
export function normalizeSocialQuery(query: string): string {
  return query
    .trim()
    .replace(/\s+/gu, " ")
    .replace(/^@/u, "")
    .replace(/^https?:\/\//iu, "")
    .replace(/^(www\.|m\.)/iu, "")
    .replace(/\/+$/u, "")
    .toLowerCase();
}

/** Comparable form of a profile link: host without "www."/"m.", path without query or slash. */
export function normalizeSocialUrl(url: string): string | null {
  try {
    const parsed = new URL(
      /^https?:\/\//iu.test(url.trim()) ? url.trim() : `https://${url.trim()}`,
    );
    const host = parsed.hostname.toLowerCase().replace(/^(www\.|m\.|web\.)/u, "");
    const path = parsed.pathname.replace(/\/+$/u, "").toLowerCase();
    // Facebook profiles without a vanity name are identified by the query.
    const id = path === "/profile.php" ? parsed.searchParams.get("id") : null;
    return `${host}${path}${id ? `?id=${id}` : ""}`;
  } catch {
    return null;
  }
}
